import { keepPreviousData } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CustomPagination,
  DirectoryHeader,
  formatDate,
  Main,
  Padding,
  Stack,
  TextEllipsis,
  UserDirectoryDto,
  useDirectory,
  useUsersQuery,
  TotalEntries,
  deserializeValidPeriod,
  serializeValidPeriodSort,
  EMPTY_PAGE,
  UserDirectoryFilterKey,
  serializeValidPeriod,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import translations from './UserDirectory.translations.json';
import { Helmet } from 'react-helmet-async';
import { UserTable } from './UserTable';
import { FormInstance } from 'antd';
import { UserDirectoryFilterPanel } from './UserDirectoryFilterPanel';

const { addTranslations, useTranslation, t } = i18nNsFactory('user-directory');
addTranslations(translations);

const filterKeys: UserDirectoryFilterKey[] = [
  'username',
  'firstName',
  'lastName',
  'roles',
  'search',
  'validFrom',
  'validTo',
  'isAdmin',
  'isSuperadmin',
  'accessGroupId',
  'groupId',
];
const numericFields: UserDirectoryFilterKey[] = [
  'roles',
  'accessGroupId',
  'groupId',
];
const arrayFields: UserDirectoryFilterKey[] = ['roles'];
const booleanFields: UserDirectoryFilterKey[] = ['isAdmin', 'isSuperadmin'];

export const UserDirectory: FC = () => {
  const { t } = useTranslation();
  const filtersRef = useRef<FormInstance>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const {
    headerProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
    sidePanelFilterProps,
  } = useDirectory({
    queryFilterKeys: filterKeys,
    arrayFields: arrayFields,
    numericFields: numericFields,
    booleanFields: booleanFields,
    sidePanelFilterRef: filtersRef,
    initialSort: [{ id: 'username', desc: false }],
    serializeFilter: (...args) => {
      const [[id, value], ...rest] = Object.entries(
        serializeValidPeriod(args[0], args[1]),
      );
      return {
        [id]: value,
        ...Object.fromEntries(rest),
      };
    },
    deserializeFilter: (id, value, allQueryFilters) =>
      deserializeValidPeriod(id, value, allQueryFilters),
    serializeSort: serializeValidPeriodSort,
  });

  const {
    data: { content: users, totalElements } = EMPTY_PAGE,
    isFetching: isUsersFetching,
  } = useUsersQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col>
          <Main.Header />
          <Main.Content padding={false} id="user-directory-content">
            <ContentWrapper>
              <DirectoryHeader
                title={t('title')}
                infoIconUrl={InfoIconUrl.USER_DIRECTORY}
                isFiltersOpen={isFilterOpen}
                onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
                {...headerProps}
              />
              <Padding horizontal bottom>
                <UserTable
                  {...tableProps}
                  data={users || []}
                  isLoading={isUsersFetching}
                  renderData={renderTableData}
                />
              </Padding>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
          {isFilterOpen && (
            <UserDirectoryFilterPanel
              ref={filtersRef}
              onClose={() => setIsFilterOpen(false)}
              initialValues={queryParameters}
              {...sidePanelFilterProps}
            />
          )}
        </Main.Col>
      </Main>
    </>
  );
};

const renderTableData = (
  key: string,
  value: string,
  { id, validFrom, validTo }: UserDirectoryDto,
  rowIndex: number,
) => {
  switch (key) {
    case 'username':
      return (
        <TextEllipsis>
          <UiLink
            id={`users-${rowIndex}-username`}
            path={path.userView}
            params={{ id }}
          >
            {value}
          </UiLink>
        </TextEllipsis>
      );
    case 'validPeriod':
      return !validFrom && !validTo
        ? ''
        : [validFrom, validTo]
            .map((dateString) => (dateString ? formatDate(dateString) : '...'))
            .join(' – ');
    case 'isAdmin':
    case 'isSuperadmin':
      return t(`${key}.${value}`);
    default:
      return undefined;
  }
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  align-self: center;
`;
